import { create } from "zustand";

import { getProfile } from "@/api/me";
import { type User, UserRole } from "shared-components";

import { useAuthStore } from "./use-auth-store";

interface UserStore {
  activeUser: User | null;
  hasNetworkError: boolean;
  hasInitializedUser: boolean;
  setUser: (user: User) => void;
  updateUser: (user: Partial<User>) => void;
  loadUserData: () => Promise<void>;
  reset: () => void;
  isAdmin: () => boolean;
}

const initialState = {
  activeUser: null,
  hasInitializedUser: false,
  hasNetworkError: false,
};

export const useUserStore = create<UserStore>((set, get) => ({
  ...initialState,
  isAdmin: () => {
    const user = get().activeUser;

    if (!user) {
      return false;
    }

    return [UserRole.DASH_ADMIN, UserRole.STORE_ADMIN].includes(user.role);
  },
  setUser: (activeUser: User) => {
    set({ activeUser, hasInitializedUser: true });
  },
  updateUser: (info: Partial<User>) => {
    set({ activeUser: <User>{ ...get().activeUser, ...info } });
  },
  async loadUserData() {
    const authState = useAuthStore.getState();

    if (get().hasInitializedUser || !authState.hasCheckedAuth) {
      return;
    }

    if (!get().activeUser && authState.isAuthenticated) {
      try {
        const activeUser = await getProfile();

        set({ activeUser, hasInitializedUser: true });
        return;
      } catch (e) {
        set({ hasNetworkError: true, hasInitializedUser: true });
        return;
      }
    }

    set({ hasInitializedUser: true });
  },
  reset: () => set(initialState),
}));
